<template>
  <NavBar />
  <BackgroundImage
    :imageUrl="require('@/assets/login/bg.png')"
    :customStyle="{ minHeight: '100vh' }"
  >
    <div class="container">
      <a-flex gap="middle" justify="center" vertical style="margin-left:-8px;">
        <a-row :gutter="[0, 24]" justify="center">
          <!-- 顶部品牌信息 -->
          <a-col :md="24" :sm="24" :xs="24">
            <a-card class="brand-card">
              <div class="brand-header">
                <div class="brand-logo">岐</div>
                <div>
                  <h1 class="brand-title">数慧岐黄 · 专药智能库</h1>
                  <p class="brand-subtitle">连接专病—专药—名医传承人的疗法枢纽</p>
                </div>
              </div>
              <div class="search-section">
                <a-input
                  v-model:value="searchKeyword"
                  size="large"
                  placeholder="搜索中药/方剂/功效/病症/体质… 例如：补气、脾虚、六味地黄丸"
                  @pressEnter="handleSearch"
                >
                  <template #prefix>
                    <SearchOutlined />
                  </template>
                </a-input>
                <a-button type="primary" size="large" @click="handleSearch">
                  搜索
                </a-button>
              </div>
              <div class="tag-chips">
                <a-tag
                  v-for="tag in filterTags"
                  :key="tag"
                  :color="activeTags.includes(tag) ? 'blue' : 'default'"
                  class="tag-chip"
                  @click="toggleTag(tag)"
                >
                  {{ tag }}
                </a-tag>
              </div>
            </a-card>
          </a-col>

          <!-- 导航锚点 -->
          <a-col :md="24" :sm="24" :xs="24">
            <div class="nav-links">
              <a-button
                v-for="link in navLinks"
                :key="link.id"
                class="nav-link"
                @click="scrollToSection(link.id)"
              >
                {{ link.label }}
              </a-button>
            </div>
          </a-col>

          <!-- Hero 区域 -->
          <a-col :md="24" :sm="24" :xs="24" id="intro">
            <a-card class="section-card">
              <a-row :gutter="[24, 24]" align="middle">
                <a-col :md="14" :sm="24" :xs="24">
                  <div class="hero-content">
                    <h2 class="hero-title">个性化方药 · 即刻可用</h2>
                    <p class="hero-desc">
                      融合传统中医理论与现代药理证据，提供可溯源、可解释、可交付的专药方案。
                    </p>
                    <div class="pill-group">
                      <span class="pill" v-for="item in heroPills" :key="item">{{ item }}</span>
                    </div>
                  </div>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="quick-card">
                    <p class="info-subtitle">快速体验</p>
                    <a-input
                      v-model:value="aiQuickForm.physique"
                      placeholder="体质（例：阴虚质/脾虚质）"
                      size="large"
                    />
                    <a-input
                      v-model:value="aiQuickForm.disease"
                      placeholder="专病（例：失眠/慢性疲劳）"
                      size="large"
                    />
                    <div class="action-buttons">
                      <a-button type="primary" @click="generateAiQuick">生成AI推荐</a-button>
                      <a-button @click="clearAiQuick">清空</a-button>
                    </div>
                    <pre v-if="aiQuickOutput" class="ai-output">{{ aiQuickOutput }}</pre>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 专药精选 -->
          <a-col :md="24" :sm="24" :xs="24" id="featured">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                专药精选（按季节·体质·专病实时推荐）
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="item in featuredList"
                  :key="item.name"
                  :md="12"
                  :sm="24"
                  :xs="24"
                >
                  <div class="card-block">
                    <div class="drug-card">
                      <div class="thumb">🧪</div>
                      <div class="drug-info">
                        <h4 class="card-title">
                          {{ item.name }}
                          <span class="tag-pill">{{ item.physique }}</span>
                        </h4>
                        <p class="info-subtitle">{{ item.hero }} · 适配：{{ item.disease }}</p>
                        <div class="tag-list">
                          <span class="tag-pill" v-for="tag in item.tags" :key="tag">{{ tag }}</span>
                        </div>
                        <p class="info-subtitle">关联名医：{{ item.doctors.join('、') }}</p>
                        <div class="action-buttons">
                          <a-button type="primary" @click="notify(`已加入调理方案：${item.name}`)">
                            加入调理方案
                          </a-button>
                          <a-button @click="notify('查看详情（示例）')">
                            查看详情
                          </a-button>
                        </div>
                      </div>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 药材知识库 -->
          <a-col :md="24" :sm="24" :xs="24" id="kb">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                药材知识库
              </h2>
              <div class="knowledge-grid">
                <div class="knowledge-card">
                  <h3 class="info-title">中药材</h3>
                  <ul class="info-list">
                    <li v-for="item in herbsList" :key="item">{{ item }}</li>
                  </ul>
                </div>
                <div class="knowledge-card">
                  <h3 class="info-title">方剂</h3>
                  <ul class="info-list">
                    <li v-for="item in formulaList" :key="item">{{ item }}</li>
                  </ul>
                </div>
                <div class="knowledge-card">
                  <h3 class="info-title">中成药</h3>
                  <ul class="info-list">
                    <li v-for="item in otcList" :key="item">{{ item }}</li>
                  </ul>
                </div>
              </div>
            </a-card>
          </a-col>

          <!-- AI 推荐 -->
          <a-col :md="24" :sm="24" :xs="24" id="ai">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                AI专药推荐（个性化匹配）
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <div class="info-card">
                    <p class="info-subtitle">输入你的体质与专病，得到基础方 + 加减思路（演示数据）。</p>
                    <div class="triage-form">
                      <a-input
                        v-model:value="aiDetailForm.physique"
                        placeholder="体质（例：脾虚质）"
                      />
                      <a-input
                        v-model:value="aiDetailForm.disease"
                        placeholder="专病（例：慢性疲劳综合征）"
                      />
                      <a-button type="primary" @click="generateAiDetail">
                        AI推荐
                      </a-button>
                    </div>
                    <pre v-if="aiDetailOutput" class="ai-output">{{ aiDetailOutput }}</pre>
                  </div>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">注意与免责声明</h3>
                    <p class="info-subtitle">
                      本页面仅提供教育与健康管理参考，不构成诊断与处方。药物使用请遵循专业医师指导。
                    </p>
                    <div class="tag-list">
                      <span class="tag-pill" v-for="item in aiNoticeTags" :key="item">{{ item }}</span>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 名医传承方 -->
          <a-col :md="24" :sm="24" :xs="24" id="masters">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                名医传承方专区
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="item in mastersList"
                  :key="item.title"
                  :md="8"
                  :sm="24"
                  :xs="24"
                >
                  <div class="card-block">
                    <div class="master-card">
                      <div class="thumb">📜</div>
                      <div class="master-info">
                        <h4 class="card-title">{{ item.title }}</h4>
                        <p class="info-subtitle">主治：{{ item.use }}</p>
                        <p class="info-subtitle">功效：{{ item.power }}</p>
                        <div class="tag-list">
                          <span class="tag-pill">{{ item.school }}</span>
                          <span class="tag-pill">传承</span>
                        </div>
                        <a-button @click="notify('AI体质匹配（示例）')">
                          AI体质匹配
                        </a-button>
                      </div>
                    </div>
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 溯源与调配 -->
          <a-col :md="24" :sm="24" :xs="24" id="trace">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                药材溯源与智能调配
              </h2>
              <a-row :gutter="[24, 24]">
                <a-col :md="14" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">产地与批次</h3>
                    <div class="map-placeholder"></div>
                    <p class="info-subtitle">
                      演示地图 · 支持接入供应链API，展示产地、批次、检验报告等信息。
                    </p>
                    <a-button @click="notify('下载质检报告（示例）')">下载质检报告</a-button>
                  </div>
                </a-col>
                <a-col :md="10" :sm="24" :xs="24">
                  <div class="info-card">
                    <h3 class="info-title">AI智能调配建议</h3>
                    <a-table
                      :columns="mixColumns"
                      :data-source="mixData"
                      :pagination="false"
                      size="small"
                      bordered
                      row-key="dimension"
                    />
                  </div>
                </a-col>
              </a-row>
            </a-card>
          </a-col>

          <!-- 用户调理方案 -->
          <a-col :md="24" :sm="24" :xs="24" id="plans">
            <a-card class="section-card">
              <h2 class="section-title">
                <span class="title-bar"></span>
                用户调理方案（与“数智郎中”联动）
              </h2>
              <a-row :gutter="[16, 16]">
                <a-col
                  v-for="item in plansList"
                  :key="item.title"
                  :md="12"
                  :sm="24"
                  :xs="24"
                >
                  <div class="card-block">
                    <h4 class="card-title">{{ item.title }}</h4>
                    <p class="info-subtitle">基础方：{{ item.base }}</p>
                    <p class="info-subtitle">加减：{{ item.add }}</p>
                    <div class="tag-list">
                      <span class="tag-pill">生活建议：{{ item.tips }}</span>
                    </div>
                    <div class="action-buttons">
                      <a-button type="primary" @click="notify('生成处方卡（示例）')">
                        生成处方卡
                      </a-button>
                      <a-button @click="notify('保存方案（示例）')">
                        保存方案
                      </a-button>
                    </div>
                  </div>
                </a-col>
              </a-row>
              <p class="footer-note">
                © 数慧岐黄 · 专药中心 — 演示页面。具体诊疗请遵循执业医师指导。
              </p>
            </a-card>
          </a-col>
        </a-row>
      </a-flex>
    </div>
  </BackgroundImage>
</template>

<script>
import BackgroundImage from "@/components/BackgroundImage.vue";
import NavBar from "@/components/NavBar.vue";
import { SearchOutlined } from "@ant-design/icons-vue";

export default {
  name: "ZhuanyaoPage",
  components: {
    BackgroundImage,
    NavBar,
    SearchOutlined,
  },
  data() {
    return {
      searchKeyword: "",
      filterTags: ["滋阴", "清热", "补气", "理气", "健脾", "调肝", "安神", "外用"],
      activeTags: [],
      navLinks: [
        { id: "intro", label: "专药简介" },
        { id: "featured", label: "专药精选" },
        { id: "kb", label: "药材知识库" },
        { id: "ai", label: "AI推荐" },
        { id: "masters", label: "名医传承方" },
        { id: "trace", label: "药材溯源" },
        { id: "plans", label: "用户方案" },
      ],
      heroPills: [
        "📦 标准化药材档案",
        "🧠 AI体质·病机匹配",
        "🧪 质检报告对接",
        "🧭 临床路径联动",
      ],
      aiQuickForm: {
        physique: "",
        disease: "",
      },
      aiQuickOutput: "",
      aiDetailForm: {
        physique: "",
        disease: "",
      },
      aiDetailOutput: "",
      featuredList: [
        {
          name: "六味地黄丸",
          hero: "滋阴补肾",
          physique: "阴虚质",
          disease: "潮热盗汗/耳鸣",
          doctors: ["刘渡舟", "朱良春"],
          tags: ["滋阴", "补肾", "肝肾同源"],
        },
        {
          name: "四君子汤",
          hero: "补气健脾",
          physique: "脾虚质",
          disease: "乏力食少/便溏",
          doctors: ["李东垣学派"],
          tags: ["补气", "健脾"],
        },
        {
          name: "柴胡疏肝散",
          hero: "疏肝理气",
          physique: "气郁质",
          disease: "胸胁胀痛/情志不舒",
          doctors: ["叶天士"],
          tags: ["理气", "疏肝", "解郁"],
        },
        {
          name: "复方丹参滴丸",
          hero: "活血化瘀",
          physique: "瘀血质",
          disease: "胸痹心痛",
          doctors: ["现代循证"],
          tags: ["活血", "冠心病辅助"],
        },
      ],
      herbsList: [
        "黄芪 · 甘、微温 · 补气固表 · 现代：免疫调节",
        "人参 · 甘、微苦、微温 · 大补元气 · 现代：抗疲劳",
        "丹参 · 苦、微寒 · 活血祛瘀 · 现代：改善微循环",
      ],
      formulaList: [
        "四君子汤（党参、白术、茯苓、甘草）— 补气健脾",
        "六味地黄丸（熟地、山萸、山药等）— 滋阴补肾",
        "柴胡疏肝散（柴胡、白芍、陈皮等）— 疏肝理气",
      ],
      otcList: [
        "参苓白术散— 健脾益气 · 慢性腹泻/食少便溏",
        "复方丹参滴丸— 活血化瘀 · 胸痹心痛",
        "牛黄清心丸— 清热化痰 · 心烦口苦",
      ],
      aiNoticeTags: ["可解释推荐", "名医证据链", "溯源批次"],
      mastersList: [
        {
          title: "朱良春肝郁化火方",
          use: "肝郁气滞、月经不调",
          power: "疏肝理气、解郁降火",
          school: "朱良春学派",
        },
        {
          title: "叶天士清胃散化裁",
          use: "胃热牙痛、口疮",
          power: "清胃泻火、凉血",
          school: "温病学派",
        },
        {
          title: "张景岳右归丸意",
          use: "命门火衰、畏寒肢冷",
          power: "温补肾阳",
          school: "景岳全书",
        },
      ],
      mixColumns: [
        { title: "维度", dataIndex: "dimension", key: "dimension", width: 140 },
        { title: "建议", dataIndex: "suggestion", key: "suggestion" },
      ],
      mixData: [
        { dimension: "体质偏颇", suggestion: "脾虚 → 甘温补气为主，兼理气醒脾" },
        { dimension: "季节/节气", suggestion: "长夏 → 健脾祛湿，适当清暑化湿" },
        { dimension: "膳食搭配", suggestion: "配山药、薏苡仁、莲子，少寒凉生冷" },
      ],
      plansList: [
        {
          title: "肝郁质 · AI调理方案",
          base: "柴胡疏肝散",
          add: "合合欢皮/香附以解郁安神",
          tips: "情志调摄 + 运动 + 早睡",
        },
        {
          title: "阴虚质 · AI调理方案",
          base: "六味地黄丸",
          add: "知母/黄柏调阴火",
          tips: "避辛辣 · 补水 · 适度运动",
        },
      ],
    };
  },
  methods: {
    handleSearch() {
      const keyword = this.searchKeyword.trim();
      const selectedTags = this.activeTags.join("、") || "全部";
      if (!keyword && !this.activeTags.length) {
        this.$message.info("请输入关键词或选择标签");
        return;
      }
      this.$message.success(`搜索“${keyword || '全部'}” · 标签：${selectedTags}（演示）`);
    },
    toggleTag(tag) {
      const index = this.activeTags.indexOf(tag);
      if (index > -1) {
        this.activeTags.splice(index, 1);
      } else {
        this.activeTags.push(tag);
      }
      const text = this.activeTags.length ? this.activeTags.join("、") : "全部";
      this.$message.info(`筛选：${text}`);
    },
    scrollToSection(id) {
      const element = document.getElementById(id);
      if (!element) {
        return;
      }
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    generateAiQuick() {
      const physique = this.aiQuickForm.physique.trim() || "脾虚质";
      const disease = this.aiQuickForm.disease.trim() || "慢性疲劳";
      this.aiQuickOutput = `【体质】${physique} · 【专病】${disease}\nAI推荐：\n1) 四君子汤 基础方 → 健脾益气\n2) 加 陈皮/砂仁 → 理气醒脾\n提示：配合作息规律与轻运动`;
    },
    clearAiQuick() {
      this.aiQuickForm.physique = "";
      this.aiQuickForm.disease = "";
      this.aiQuickOutput = "";
    },
    generateAiDetail() {
      const physique = this.aiDetailForm.physique.trim() || "脾虚质";
      const disease = this.aiDetailForm.disease.trim() || "慢性疲劳综合征";
      this.aiDetailOutput = `【体质】${physique} · 【专病】${disease}\n推荐组合：党参 + 白术 + 茯苓 + 甘草（四君子汤）\n可加：陈皮、砂仁（理气醒脾）\n警示：孕期、慢病合并人群需遵医嘱。`;
    },
    notify(message) {
      this.$message.info(message);
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  padding: 80px 20px 40px;
  min-height: 100vh;
}

.brand-card {
  border-radius: 16px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(16, 65, 181, 0.12);

  :deep(.ant-card-body) {
    padding: 32px;

    @media (max-width: 768px) {
      padding: 24px;
    }
  }
}

.section-card {
  border-radius: 16px;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 8px 32px rgba(16, 65, 181, 0.12);

  :deep(.ant-card-body) {
    padding: 32px;

    @media (max-width: 768px) {
      padding: 24px;
    }
  }
}

.brand-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.brand-logo {
  width: 64px;
  height: 64px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eed36b, #c9a227);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: #2d6a4f;
  box-shadow: 0 4px 16px rgba(16, 65, 181, 0.15);
}

.brand-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #1041b5;
}

.brand-subtitle {
  margin: 0;
  color: #64748b;
  font-size: 14px;
}

.search-section {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.tag-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-chip {
  cursor: pointer;
  border-radius: 999px;
  padding: 6px 14px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(94, 123, 243, 0.25);
  }
}

.nav-links {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;

  .nav-link {
    border-radius: 999px;
    border: 1px solid rgba(94, 123, 243, 0.2);
    background: rgba(255, 255, 255, 0.9);
    color: #1041b5;
    padding: 4px 12px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(135deg, rgba(94, 123, 243, 0.12), rgba(16, 65, 181, 0.1));
      border-color: #5e7bf3;
      transform: translateY(-2px);
    }
  }
}

.section-title {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 22px;
  font-weight: 700;
  color: #1041b5;
  margin: 0 0 24px;

  @media (max-width: 768px) {
    font-size: 19px;
  }
}

.title-bar {
  width: 8px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(180deg, #c9a227 0%, #eed36b 100%);
  box-shadow: 0 2px 8px rgba(201, 162, 39, 0.3);
}

.hero-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hero-title {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #1041b5;
}

.hero-desc {
  margin: 0;
  color: #445069;
  font-size: 15px;
  line-height: 1.7;
}

.pill-group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(94, 123, 243, 0.08), rgba(16, 65, 181, 0.06));
  border: 1px dashed rgba(201, 162, 39, 0.6);
  color: #2d6a4f;
  font-size: 13px;
}

.quick-card {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid rgba(94, 123, 243, 0.15);
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 6px 24px rgba(16, 65, 181, 0.08);
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.ai-output {
  background: rgba(94, 123, 243, 0.08);
  border: 1px solid rgba(94, 123, 243, 0.2);
  border-radius: 12px;
  padding: 16px;
  font-size: 13px;
  color: #2d3436;
  margin: 0;
  white-space: pre-wrap;
}

.card-block {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid rgba(94, 123, 243, 0.15);
  padding: 24px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 6px 24px rgba(16, 65, 181, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 14px 32px rgba(16, 65, 181, 0.18);
    border-color: rgba(94, 123, 243, 0.35);
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
}

.card-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #1041b5;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.tag-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(94, 123, 243, 0.08);
  color: #1041b5;
  font-size: 12px;
  border: 1px solid rgba(94, 123, 243, 0.2);
}

.drug-card {
  display: grid;
  grid-template-columns: 96px 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 72px 1fr;
  }
}

.thumb {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  background: linear-gradient(135deg, #eef2ff, #f8fafc);
  border: 1.5px solid rgba(94, 123, 243, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  color: #1041b5;
}

.info-card {
  background: #fff;
  border-radius: 14px;
  border: 1.5px solid rgba(94, 123, 243, 0.15);
  padding: 24px;
  height: 100%;
  box-shadow: 0 6px 24px rgba(16, 65, 181, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 12px 32px rgba(16, 65, 181, 0.16);
    border-color: rgba(94, 123, 243, 0.35);
  }
}

.info-title {
  font-size: 18px;
  font-weight: 700;
  color: #1041b5;
  margin: 0 0 12px;
}

.info-subtitle {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 12px;
  line-height: 1.7;
}

.info-list {
  margin: 0;
  padding-left: 18px;
  color: #445069;
  font-size: 14px;
  line-height: 1.7;
}

.knowledge-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
}

.knowledge-card {
  background: #fff;
  border-radius: 12px;
  border: 1.5px solid rgba(94, 123, 243, 0.15);
  padding: 20px;
}

.triage-form {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.master-card {
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 16px;
  align-items: center;
}

.master-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.map-placeholder {
  width: 100%;
  height: 220px;
  border-radius: 12px;
  border: 1.5px solid rgba(94, 123, 243, 0.2);
  background:
    radial-gradient(circle at 30% 60%, rgba(229, 244, 234, 0.8), transparent 40%),
    radial-gradient(circle at 70% 30%, rgba(237, 231, 210, 0.85), transparent 50%),
    repeating-linear-gradient(0deg, transparent 0 18px, rgba(0, 0, 0, 0.05) 18px 19px),
    repeating-linear-gradient(90deg, transparent 0 18px, rgba(0, 0, 0, 0.05) 18px 19px);
}

.footer-note {
  margin-top: 24px;
  text-align: center;
  font-size: 12px;
  color: #64748b;
}

:deep(.ant-table) {
  font-size: 13px;

  .ant-table-thead > tr > th {
    background: rgba(94, 123, 243, 0.1);
    color: #1041b5;
    font-weight: 600;
  }

  .ant-table-tbody > tr:hover > td {
    background: rgba(94, 123, 243, 0.05);
  }
}

/* 统一按钮样式，与 home 页面保持一致 */
:deep(.ant-btn) {
  border-radius: 16px !important;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  box-shadow: none;
}
</style>
